/* 類別選擇頁（Category Hub / 等尺寸格線）— 側欄「功能分區」五個大主題的落地頁。
 * 設計稿：docs/design/sidebar-bento-hub.html（使用者確認版，視覺與資料結構忠實移植）。
 *
 * 路由：#/cat/<key>，key ∈ daily | quant | research | tools | erp
 *   （_router.jsx parseRoute 只取第一段 'cat'，第二段由本頁自行讀 hash；
 *    與 #/stock-studio/2330 同一種兩段寫法。）
 *
 * 權限：卡片可見性**完全沿用**現行判定，本檔不新增任何規則——
 *   • 站內路由卡  → canAccessRoute(user, key)（等同舊側欄 canRoute）
 *   • 台股掃描器  → canAccessRoute(user, 'research')（舊側欄同 gate）
 *   • GitHub / 軟體發佈中心 → canAccessToolLinks(user)
 *   • ERP 五段    → !isRestrictedAccount(user) && canAccessRoute(user, key)
 *     （含「團隊與工程」的站會帳本 / 交易部週報 / 模型評測 / 團隊雷達外開卡：route
 *      key 不在任何非員工路由白名單、後端 /api/progress、/api/trader-report、
 *      /api/llm-judge、/api/team-radar 亦不在任何非員工前綴白名單 → 員工限定，
 *      與 ERP 整區 gate 一致）
 *   看不到的卡片不渲染；某分區一張都看不到 → 側欄該大主題隱藏（TradingSidebar
 *   讀 window.CategoryHub.visibleItems）、直連本頁則顯示「無可用功能」空狀態。
 *
 * 外開卡片一律呼叫既有 hr-shell.jsx open*Tab helper（同步 window.open 過
 * popup blocker → 換 cookie → 導向），href 僅作中鍵/Ctrl+click 後備。
 *
 * 依賴（window globals）：React / VA2 / FontSizes / Icon / useToast /
 * canAccessRoute / canAccessToolLinks / isRestrictedAccount / open*Tab。
 * 版面 class（va2-cat-*）在 index.html 全域 <style>。
 */

(function () {

/* Expressive 外殼專屬覆寫（plan-expressive-inner-pages.md §2(c)-6，P5 擁有）：只在
 * <html data-ui-shell="expressive"> 時注入（GxCore.injectCss 在 Bento 為 no-op），每條選擇器
 * 皆以 html[data-ui-shell="expressive"] 開頭（tests/helpers/gx_css_guard.cjs 守門）。
 * index.html 的 .va2-cat-* 與 feature-preview.css 為 Bento 共用，一律不改、只在此覆寫。
 * .va2-cat-go 只在 Tile 的 inline uiStyle 處理（審查 #8：單一處理點）。 */
GxCore.injectCss('gx-css-p-cathub', String.raw`
html[data-ui-shell="expressive"] .va2-cat-page[data-gx-t] .va2-cat-head{display:flex;align-items:center;gap:18px;flex-wrap:wrap;padding:22px 24px!important;margin-bottom:20px;border-radius:16px;background:var(--gx-cbg);color:var(--gx-con)}
html[data-ui-shell="expressive"] .va2-cat-page[data-gx-t] .va2-cat-head > div:not(.va2-cat-meta){flex:1 1 320px;min-width:0}
html[data-ui-shell="expressive"] .gx-cathub-ic{width:56px;height:56px;flex:none;display:grid;place-items:center;border-radius:50%;background:var(--gx-card);color:var(--gx-cacc)}
html[data-ui-shell="expressive"] .va2-cat-page[data-gx-t] .va2-cat-meta{gap:20px;padding-bottom:0;font-size:13px;font-weight:600;align-self:flex-end}
html[data-ui-shell="expressive"] .va2-cat-page[data-gx-t] .va2-cat-meta b{font:900 22px/1 var(--gx-font);color:inherit;margin-right:2px;font-variant-numeric:tabular-nums}
html[data-ui-shell="expressive"] .va2-cat-bento{gap:16px}
html[data-ui-shell="expressive"] .va2-cat-tile{border:0;border-radius:16px;background:var(--gx-card);transition:border-radius .45s var(--gx-spring),background .2s}
html[data-ui-shell="expressive"] .va2-cat-tile:hover{border-radius:10px;background:var(--gx-s1);background:color-mix(in srgb,var(--gx-cbg,var(--gx-s2)) 45%,var(--gx-card));box-shadow:none;transform:none}
html[data-ui-shell="expressive"] .va2-cat-tile:active{transform:scale(.98)}
html[data-ui-shell="expressive"] .va2-cat-tile:focus-visible{outline:3px solid var(--gx-p);outline-offset:2px}
html[data-ui-shell="expressive"] .va2-cat-tx{border-top:0;padding:14px 16px}
html[data-ui-shell="expressive"] .va2-cat-tt::before{border-radius:50%;background:var(--gx-cacc,var(--gx-p))}
html[data-ui-shell="expressive"] .va2-cat-td{font-size:13px}
html[data-ui-shell="expressive"] .va2-cat-subhead{margin-top:14px}
html[data-ui-shell="expressive"] .va2-cat-subhead i{background:var(--gx-olv)!important}
html[data-ui-shell="expressive"] .va2-cat-preview-trigger{border:0;border-radius:10px;min-height:32px;padding:0 14px;background:var(--gx-sc);color:var(--gx-on-sc);font-family:var(--gx-font);font-weight:700;font-size:13px;transition:border-radius .4s var(--gx-spring)}
html[data-ui-shell="expressive"] .va2-cat-preview-trigger:hover{background:var(--gx-sc);background-image:linear-gradient(var(--gx-state-hover),var(--gx-state-hover));border-radius:8px}
html[data-ui-shell="expressive"] .va2-cat-preview-trigger:focus-visible{outline:3px solid var(--gx-p);outline-offset:2px}
html[data-ui-shell="expressive"] .va2-feature-preview{border:0;border-radius:16px;padding:20px;background:var(--gx-card);color:var(--gx-on);font-family:var(--gx-font);box-shadow:var(--gx-sh-2)}
html[data-ui-shell="expressive"] .va2-feature-preview-head h2{font-size:20px;font-weight:800;font-family:var(--gx-font)}
html[data-ui-shell="expressive"] .va2-feature-preview-head button{width:40px;height:40px;border:0;border-radius:12px;background:var(--gx-s2);color:var(--gx-on)}
html[data-ui-shell="expressive"] .va2-feature-preview button:focus-visible{outline:3px solid var(--gx-p);outline-offset:2px}
html[data-ui-shell="expressive"] .va2-feature-preview .va2-feature-preview-caption{color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .va2-feature-preview-start{border-radius:12px;padding:14px 16px;background:var(--gx-pc);color:var(--gx-on-pc)}
html[data-ui-shell="expressive"] .va2-feature-preview-start strong{color:var(--gx-on-pc);font-weight:800}
html[data-ui-shell="expressive"] .va2-preview-backdrop{background:var(--gx-scrim)}
html[data-ui-shell="expressive"] .va2-preview-screenshot img{border:0;border-radius:12px;background:var(--gx-s2)}
html[data-ui-shell="expressive"] .va2-preview-screenshot figcaption{color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .va2-preview-enlarge span{border-radius:8px;font-weight:700}
html[data-ui-shell="expressive"] .va2-preview-unavailable{border:0;border-radius:12px;background:var(--gx-s2);color:var(--gx-on-v)}
@media (max-width:760px){
html[data-ui-shell="expressive"] .va2-cat-page[data-gx-t] .va2-cat-head{padding:18px!important;gap:14px}
html[data-ui-shell="expressive"] .va2-cat-page[data-gx-t] .va2-cat-head > div:not(.va2-cat-meta){flex:1 1 200px}
html[data-ui-shell="expressive"] .gx-cathub-ic{width:48px;height:48px}
html[data-ui-shell="expressive"] .va2-cat-page[data-gx-t] .va2-cat-meta{align-self:auto;flex-wrap:wrap;gap:6px 16px}
html[data-ui-shell="expressive"] .va2-cat-bento{gap:12px}
}
@media (prefers-reduced-motion:reduce){
html[data-ui-shell="expressive"] .va2-cat-tile,html[data-ui-shell="expressive"] .va2-cat-preview-trigger{transition:none}
html[data-ui-shell="expressive"] .va2-cat-tile:active{transform:none}
}
`);

/* ─────────────  資料：五個分區（順序 = 側欄順序）  ─────────────
 * 分區欄位：hue=分區主色（縮圖線色／標題色塊／hover 邊框／eyebrow）、tint=縮圖淡底。
 * 卡片欄位：key=route key（可見性 gate；無 key 者用 gate）、n=名稱、hash=站內路由、
 * href=外開網址（後備）、open=hr-shell.jsx helper 名、p=縮圖種類、b=標籤文字、
 * ext=外開、lock=員工限定、admin=僅 admin、d=描述（卡片等尺寸，描述固定 2 行
 * clamp，請控制在約 40 字內）、icon=側欄「常用」直達區用的 nav icon（只有
 * QUICK_ACCESS_KEYS 內的項目需要；卡片本身不用）、kw=⌘K 搜尋同義詞（選填，
 * 不顯示在卡片上；只讓 hr-cmdk.jsx searchPages 多一個比對欄位，可見性仍由
 * buildPageIndex 依 canAccessRoute / itemVisible 決定，不會因 kw 露出無權頁面）。 */
const CATEGORIES = [
  { key: 'daily', name: '每日情報', icon: 'sun', hue: '#9A6A1E', tint: '#F6EEDD',
    eyebrow: 'Daily Intelligence',
    lead: '開盤前到收盤後的固定讀物。每天同一個順序看完，新聞與報告來自 11.90 的 OCR / 新聞管線，分點資料來自 11.37 收集端。',
    items: [
      { key: 'morning-focus', n: '盤前重點', hash: '#/morning-focus', p: 'agenda', b: ['NEW'],
        d: '開盤前該看的三件事：隔夜美股與夜盤、今日事件表、值得注意的個股，時間軸卡片排列。',
        kw: ['早晨重點', '夜盤'] },
      // 分點調查局：員工限定（route key 不在任何非員工路由白名單，canAccessRoute 自然擋下；
      // lock 只是卡片標籤）
      { key: 'fendian', n: '分點調查局', hash: '#/fendian', p: 'table', lock: true, b: ['NEW'],
        d: '波段常勝軍榜單、自選券商買賣超 Top15、共同動向與大盤籌碼，每日前一交易日彙整。',
        kw: ['券商分點', '主力', '買賣超', '籌碼'] },
      { key: 'daily-brief', n: '市場新聞', hash: '#/daily-brief', p: 'headlines',
        d: '即時新聞流，依重要度分級。標題列表加來源與時間戳，點開展開摘要。',
        kw: ['新聞', '每日重點', '快訊', '頭條'] },
      { key: 'morning-report', n: '早晨報告', hash: '#/morning-report', p: 'doc',
        d: '研究所晨會版 PDF 轉 Markdown 閱讀頁，左目錄右正文。',
        kw: ['晨報', '晨會'] },
      { key: 'analysis-report', n: '研究報告庫', hash: '#/analysis-report', p: 'thumbs',
        d: '券商報告依日期與標的分頁瀏覽，中英原文分頁切換。',
        kw: ['分析報告', '券商報告', '研報'] },
      { key: 'market-calendar', n: '台美股市行事曆', href: '/api/market-calendar/', open: 'openMarketCalendarTab', p: 'calendar', ext: true, b: ['NEW'],
        d: '台股與美股的經濟數據、財報、除權息與休市日整合月曆，點事件看細節與來源。',
        kw: ['財報', '除權息', '除息', '休市', '法說'] },
    ] },
  { key: 'quant', name: '個股與量化', icon: 'bar-chart', hue: '#0B5F5A', tint: '#E6EEED',
    eyebrow: 'Stocks & Quant',
    lead: '圍繞單一標的的深挖工具，加上幾個獨立跑的量化服務。外開服務都用 ↗ 標示，點了在新分頁開。',
    items: [
      { key: 'tjcoach', n: '交易檢討 TJCoach', href: '#/cat/quant', open: 'openTJCoachTab', p: 'candles', ext: true, b: ['NEW'],
        d: '匯入自己的交易資料，檢視交易紀錄、績效與檢討；管理員可切換瀏覽所有帳戶。' },
      { key: 'stock-studio', n: '個股工作室', hash: '#/stock-studio', p: 'candles', b: ['NEW'],
        d: '輸入代號一頁看完：K 線與量、基本面、籌碼面（主力分點集中度）、法人買賣。quantdata 直取。',
        kw: ['股票', '查股票', '股價', 'K線', '基本面', '籌碼'] },
      { key: 'quantdata', n: '量化資料庫', href: '/api/quantdata/', open: 'openQuantDataTab', p: 'table', ext: true, b: ['analyst_lite 不可'],
        d: 'quantdata.lan 的完整查詢介面，表格與 dashboard 直接代理進來。' },
      { key: 'insider', n: '內部人監測', href: '/api/insider/', open: 'openInsiderTab', p: 'avatars', ext: true,
        d: '董監持股申報異動追蹤，依人依公司列出近期買賣。',
        kw: ['董監', '持股申報'] },
      { key: 'txo-board', n: '選擇權籌碼看板', href: '/api/txo-board/', open: 'openTxoBoardTab', p: 'bars', ext: true, b: ['NEW'],
        d: '台指選擇權各履約價未平倉與 signed GEX 梯形圖，每分鐘自動更新。',
        kw: ['TXO', '台指', '未平倉'] },
      { key: 'fund-radar', n: '基金持股雷達', href: '/api/fund-radar/', open: 'openFundRadarTab', p: 'grid', ext: true, b: ['NEW'],
        d: '國內股票型／平衡型基金每月前十大持股，看共識股、產業趨勢與加減碼動向。',
        kw: ['基金', '投信', '持股', 'SITCA'] },
      { key: 'bigday', n: 'BIGDAY 明日波動預估', href: '/api/bigday/', open: 'openBigdayTab', p: 'line', ext: true, b: ['NEW'],
        d: '台灣 50 成分股明日大波動預估、觀察價位與實戰成績，每晚 20:45 更新。',
        kw: ['波動', '台灣50', '0050', '大波動', '觀察價位'] },
      { key: 'kelly-game', n: '凱利訓練場', href: '/api/kelly/', open: 'openKellyGameTab', p: 'bars', ext: true, b: ['NEW'],
        d: '用凱利公式練下注比例的互動遊戲，看資金曲線變化。',
        kw: ['下注', '資金管理'] },
      // 台股掃描器無自身 route key，可見性沿用 canRoute('research')（與舊側欄相同）
      { gate: 'research', previewKey: 'scanner', n: '台股掃描器', href: 'https://alethia-unservable-detailedly.ngrok-free.dev/position-vote', open: 'openScannerTab', p: 'line', ext: true,
        d: '依條件掃全市場，輸出候選名單與部位投票。' },
    ] },
  { key: 'research', name: '深度研究', icon: 'compass', hue: '#3B4F8A', tint: '#E7EAF3',
    eyebrow: 'Deep Research',
    lead: '從問題出發的研究工具：問答、追蹤名單、影音與文件知識庫。研究引擎跑得久，是員工限定。',
    items: [
      { key: 'research', n: 'Deep Research', hash: '#/research', p: 'chat',
        d: '輸入問題，跨新聞、報告、貼文檢索後產出有引用的長篇回答，答案可展開來源。' },
      // 2026-09-16 起改整站代理外開（/api/infoscout-site/）；站內 #/expert-tracker 保留為備援
      { key: 'expert-tracker', n: '大神追蹤器', href: '/api/infoscout-site/', open: 'openExpertTrackerTab', p: 'feed', ext: true,
        d: '追蹤名單上的 X / YouTube 作者最新貼文流，可一鍵送去深入研究。' },
      { key: 'ytkb', n: '影音知識庫', href: '/api/ytkb/dashboard/', open: 'openYtkbTab', p: 'thumbs', ext: true, b: ['NEW'],
        d: 'YouTube 影片轉教材，依頻道與主題整理的卡片牆。' },
      { key: 'files', n: '文件知識庫', hash: '#/files', p: 'folders',
        d: '內部文件分類瀏覽與全文搜尋，資料夾樹加預覽。',
        kw: ['檔案', '資料夾', '共用文件'] },
      { key: 'research-engine', n: '研究引擎', href: '/api/research-engine/', open: 'openResearchEngineTab', p: 'pipeline', ext: true, lock: true, b: ['NEW'],
        d: '長跑型研究任務（deep 端點最長 30 分鐘），提交後追蹤進度、下載報告。' },
    ] },
  { key: 'tools', name: '工具', icon: 'tool', hue: '#4A5048', tint: '#ECECE8',
    eyebrow: 'Tools',
    lead: '日常用得到但和研究無關的東西。知識庫問答已改成右下角浮動聊天室，不放這裡。',
    items: [
      { key: 'ai-center', n: 'AI 應用工具', hash: '#/ai-center', p: 'grid',
        d: '公司內部 AI 工具集合：文件摘要、翻譯、表格抽取等，每個工具一張卡、獨立操作頁。' },
      { key: 'lunch', n: '午餐訂購', hash: '#/lunch', p: 'menu', icon: 'coffee',
        d: '每日菜單 OCR 後的品項清單，勾選送出，截止時間倒數。',
        kw: ['訂餐', '便當', '點餐', '菜單'] },
      { key: 'courses', n: '教學', hash: '#/courses', p: 'lessons',
        d: '內訓課程與影片，依章節排列，記錄看到哪。',
        kw: ['課程', '教育訓練', '內訓', '教學影片'] },
      { gate: 'toolLinks', previewKey: 'github', n: 'GitHub', href: 'https://github.com/GSINVEST', p: 'commits', ext: true,
        d: 'GSINVEST 組織首頁。' },
      { gate: 'toolLinks', previewKey: 'release-hub', n: '軟體發佈中心', href: '/api/release-hub/', open: 'openReleaseHubTab', p: 'versions', ext: true,
        d: '內部軟體版本與安裝檔下載，依產品列版本紀錄。' },
    ] },
  { key: 'erp', name: 'ERP 系統', icon: 'briefcase', erp: true, hue: '#8E3B36', tint: '#F4E7E5',
    eyebrow: 'ERP',
    lead: '人資、財務與團隊內部工具。依「日常 / 人員管理 / 財務 / 管理 / 團隊與工程」五段排列，段內順序與現行側欄相同；看不到的頁面卡片不會出現。',
    sections: [
      { name: '日常', items: [
        { key: 'attendance', n: '出勤', hash: '#/attendance', p: 'calendar', icon: 'clock',
          d: '月曆檢視打卡與請假，缺卡與遲到用色塊標示，毛工時另計午休。',
          kw: ['打卡', '補打卡', '上班', '下班', '遲到', '缺卡', '考勤', '工時'] },
        { key: 'leave', n: '假勤申請', hash: '#/leave', p: 'checklist', icon: 'calendar',
          d: '選假別、填區間、送審。下方是自己的審核歷程。',
          kw: ['請假', '休假', '特休', '假單', '病假', '事假', '無薪假', '加班', '補班'] },
        { key: 'expense', n: '報帳', hash: '#/expense', p: 'receipt',
          d: '上傳收據 OCR 自動帶金額，逐筆送審。',
          kw: ['請款', '報銷', '費用', '收據', '單據'] },
        { key: 'calendar', n: '行事曆', hash: '#/calendar', p: 'calendar',
          d: '公司行事曆與國定假日、補班日。',
          kw: ['國定假日', '假日', '補班日', '連假', '日曆'] },
      ] },
      { name: '人員管理', items: [
        { key: 'employees', n: '員工', hash: '#/employees', p: 'avatars', admin: true,
          d: '員工名冊、到離職日、部門與角色設定。' },
        { key: 'departments', n: '部門', hash: '#/departments', p: 'folders', admin: true,
          d: '部門樹與主管指派。' },
        { key: 'reports', n: '報表中心', hash: '#/reports', p: 'table', b: ['finance'],
          d: '出勤、薪資、請假統計報表匯出。' },
      ] },
      { name: '財務', items: [
        { key: 'payroll', n: '薪資', hash: '#/payroll', p: 'payslip',
          d: '月薪資單：本薪、加減項、午餐津貼、請假扣款逐列列出。一般員工只看自己的。',
          kw: ['薪水', '薪資單', '薪資條', '薪條', '工資', '勞健保', '勞保', '健保', '勞退'] },
        { key: 'invoices', n: '發票・總帳', hash: '#/invoices', p: 'table', b: ['finance', 'hr'],
          d: '進銷項發票與總帳分錄，依期別篩選。',
          kw: ['會計', '分錄', '傳票'] },
      ] },
      { name: '管理', items: [
        { key: 'roles', n: '角色 & 權限', hash: '#/roles', p: 'toggles', admin: true,
          d: '角色 allowlist 與帳號指派。',
          kw: ['帳號', '使用者', 'RBAC'] },
        { key: 'audit', n: '審計日誌', hash: '#/audit', p: 'log', admin: true,
          d: '誰在何時改了什麼，可依帳號與物件篩選。' },
        { key: 'admin-tools', n: 'Token 使用', hash: '#/admin-tools', p: 'bars', admin: true,
          d: '各 AI 服務的 token 用量與費用。' },
      ] },
      // 團隊與工程（2026-09-15；09-24 加團隊雷達；10-01 加交易部週報）：四個內網站經
      // portal 代理外開（routes_progress.py / routes_trader_report.py /
      // routes_llm_judge.py / routes_team_radar.py），員工限定
      // ——ERP 整區 gate（!isRestrictedAccount）＋ canAccessRoute（route key 不在任何
      // 非員工白名單）自然套用，不另加 lock 標籤。
      { name: '團隊與工程', items: [
        { key: 'progress-ledger', n: '站會帳本', href: '/api/progress/', open: 'openProgressLedgerTab', p: 'agenda', ext: true, b: ['NEW'],
          d: 'Slack 站會回報彙整：出勤、週報、月報與焦點追蹤，一頁看完團隊進度。' },
        // 交易部週報（gs-trader-report）：無「交易」分區，與同屬團隊回報的站會帳本並列
        { key: 'trader-report', n: '每周稽核表', href: '/api/trader-report/', open: 'openTraderReportTab', p: 'doc', ext: true, b: ['NEW'],
          d: '交易員填寫每周稽核表，主管查看繳交紀錄並逐份簽核。' },
        { key: 'llm-judge', n: '模型評測', href: '/api/llm-judge/', open: 'openLlmJudgeTab', p: 'bars', ext: true, b: ['NEW'],
          d: 'llm-judge 模型評測 Dashboard：runs、缺陷、MoE 與 Colibri A/B 結果。' },
        { key: 'team-radar', n: '團隊雷達', href: '/api/team-radar/', open: 'openTeamRadarTab', p: 'line', ext: true, b: ['NEW'],
          d: 'gs-team-radar：AI 用量自查交件口（下載自查工具、上傳彙總）與主管端開發協作能力雷達（主管密碼另行登入）。' },
      ] },
    ] },
];

/* ─────────────  可見性（沿用 hr-shell.jsx 既有函式；helper 不存在時一律拒絕）  ───────────── */
function _restricted(user) {
  return window.isRestrictedAccount ? window.isRestrictedAccount(user) : false;
}
function _canRoute(user, key) {
  return window.canAccessRoute ? window.canAccessRoute(user, key) : false;  // fail-closed if hr-shell not loaded
}
function _canToolLinks(user) {
  return window.canAccessToolLinks ? window.canAccessToolLinks(user) : false;
}
function itemVisible(user, item, cat) {
  if (cat.erp && _restricted(user)) return false;   // 舊側欄：!restricted && visibleErpRoutes.length
  if (item.gate === 'toolLinks') return _canToolLinks(user);
  return _canRoute(user, item.gate || item.key);
}
function allItems(cat) {
  return cat.items || (cat.sections || []).reduce((acc, s) => acc.concat(s.items), []);
}
/* 該使用者在某分區看得到的卡片（扁平）；分區不存在 → [] */
function visibleItems(user, catKey) {
  const cat = CATEGORIES.find((c) => c.key === catKey);
  if (!cat) return [];
  return allItems(cat).filter((it) => itemVisible(user, it, cat));
}
/* route key → 所屬分區 key（側欄 active 用；只看有 key 的項目） */
function categoryOfRoute(routeKey) {
  if (!routeKey) return null;
  const cat = CATEGORIES.find((c) => allItems(c).some((it) => it.key === routeKey));
  return cat ? cat.key : null;
}
/* 目前 hash 第二段（#/cat/<key>）；非 cat 路由 → null */
function currentKey() {
  const segs = (window.location.hash || '').replace(/^#\/?/, '').split(/[/?]/);
  return segs[0] === 'cat' ? (segs[1] || '') : null;
}

/* ─────────────  側欄「常用」直達區（TradingSidebar 讀）  ─────────────
 * 常用清單只在這裡維護：順序 = 側欄順序。每個 key 必須是 CATEGORIES 內某張卡的
 * key，側欄用 findItem 取名稱／hash／icon／描述，用 itemVisible 判可見性
 * （與類別頁卡片同一套，ERP 項自然含 !isRestrictedAccount）。 */
const QUICK_ACCESS_KEYS = ['attendance', 'leave', 'lunch'];
/* route key → { item, cat }（cat = 所屬分區，itemVisible 需要）；找不到 → null */
function findItem(key) {
  for (const cat of CATEGORIES) {
    const item = allItems(cat).find((it) => it.key === key);
    if (item) return { item, cat };
  }
  return null;
}

/* ─────────────  分區色 palette  ─────────────
 * 每個分區以 hue（主色）推導縮圖用的四個色階；tint（縮圖淡底）直接由 CSS 變數吃。
 *   line     = hue                          主線／強調（舊 ACC）
 *   lineSoft = rgba(hue, .28)               次要柱狀（舊 ACC2）
 *   faint    = rgba(hue, .18)               格線／佔位（舊 FAINT）
 *   ink      = rgba(hue × .5 混黑, .82)     文字狀色塊／mono 數字（舊 INK）
 * 「跌／異常」語意色 pvDown() / pvWarn() 是資料語意，不隨分區換色。
 * 縮圖是 innerHTML 的 SVG 屬性字串、且 _rgb 要解析 hex → 一律經 GxCore.hex() 取具體色
 * （Bento 原值直通；Expressive 依目前主題），CategoryHubPage／Tile 以 GxCore.useTheme() 在換主題時重繪。
 * Expressive：hue／tint 改指 PALETTE 的分區 token（--gx-<key>-acc／-bg，淺色值與 CATEGORIES
 * 字面值相同，tests/test_gx_dark_shell.cjs 對拍）；line 為 hue 的具體色（淺色同值）；暗色的 ink
 * 改用分區 on 色（hue 混黑在暗底上讀不到），淺色公式不變。 */
function _rgb(hex) { const n = parseInt(String(hex).slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
function paletteOf(cat) {
  const hue = (cat && cat.hue) ? uiPick(cat.hue, `var(--gx-${cat.key}-acc)`) : VA2.accent;
  const line = GxCore.hex(hue);
  const [r, g, b] = _rgb(line);
  const a = (al) => `rgba(${r},${g},${b},${al})`;
  let ink = `rgba(${Math.round(r * .5)},${Math.round(g * .5)},${Math.round(b * .5)},.82)`;
  if (GxCore.theme() === 'dark' && cat && cat.key) {
    const [ir, ig, ib] = _rgb(GxCore.hex(`${cat.key}-on`));
    ink = `rgba(${ir},${ig},${ib},.82)`;
  }
  return { hue, line, lineSoft: a(.28), faint: a(.18), ink };
}

/* ─────────────  SVG 縮圖產生器（照設計稿 PV 搬入，回傳 SVG 字串）  ─────────────
 * 每個產生器簽名 (p, seed)：p = paletteOf(cat)，seed 只給需要亂數的圖。 */
function pvWarn() { return GxCore.hex(VA2.warn); }
// 縮圖內的「紙」（白卡 #fff）與強調色上的勾勾（#fff）：Expressive 走 card／on-p token（淺色同為白）。
function pvPaper() { return uiPick('#fff', GxCore.hex('card')); }
function pvOnLine() { return uiPick('#fff', GxCore.hex('on-p')); }
function pvDown() { return GxCore.hex(VA2.down); }
function rng(seed) { let s = (seed * 9301 + 49297) % 233280; return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; }; }
const svgWrap = (inner) => `<svg viewBox="0 0 320 180" preserveAspectRatio="xMidYMid slice" aria-hidden="true">${inner}</svg>`;
// K 線縮圖的漲跌色（render 時呼叫）：Bento 漲 = 分區色、跌 = pvDown()（原值）；Expressive 台股慣例漲紅跌綠（SPEC §1.2）
function pvMktColors(p) { return [uiPick(p.line, GxCore.hex(GxT.color.mktUp)), uiPick(pvDown(), GxCore.hex(GxT.color.mktDown))]; }
const gridLines = (p) => { let g = ''; for (let y = 30; y < 180; y += 30) g += `<line x1="0" y1="${y}" x2="320" y2="${y}" stroke="${p.faint}" stroke-width="1"/>`; return g; };

const PV = {
  line(p, seed) { const r = rng(seed); let pts = [], y = 110; for (let x = 0; x <= 320; x += 20) { y = Math.max(30, Math.min(150, y + (r() - 0.48) * 34)); pts.push([x, y]); }
    const d = pts.map((p, i) => (i ? 'L' : 'M') + p[0] + ' ' + p[1]).join(' ');
    return svgWrap(`${gridLines(p)}<path d="${d} L320 180 L0 180Z" fill="${p.line}" opacity=".10"/><path d="${d}" stroke="${p.line}" stroke-width="2" fill="none"/><circle cx="${pts[pts.length - 1][0]}" cy="${pts[pts.length - 1][1]}" r="4" fill="${p.line}"/>`); },
  candles(p, seed) { const r = rng(seed); const [upC, dnC] = pvMktColors(p); let s = '', c = 90; for (let x = 14; x < 320; x += 17) { const o = c, cl = Math.max(30, Math.min(160, c + (r() - 0.5) * 30)); const hi = Math.min(o, cl) - r() * 10, lo = Math.max(o, cl) + r() * 10; const up = cl < o; const col = up ? upC : dnC;
      s += `<line x1="${x}" y1="${hi}" x2="${x}" y2="${lo}" stroke="${col}" stroke-width="1.2"/><rect x="${x - 5}" y="${Math.min(o, cl)}" width="10" height="${Math.max(2, Math.abs(cl - o))}" fill="${up ? col : pvPaper()}" stroke="${col}" stroke-width="1.2"/>`; c = cl; }
    let v = ''; for (let x = 14; x < 320; x += 17) { const h = 8 + r() * 22; v += `<rect x="${x - 5}" y="${180 - h}" width="10" height="${h}" fill="${p.lineSoft}"/>`; }
    return svgWrap(gridLines(p) + v + s); },
  headlines(p, seed) { const r = rng(seed); let s = ''; for (let i = 0; i < 5; i++) { const y = 18 + i * 33; const w = 120 + r() * 150; const lvl = i === 0 ? p.line : (i === 2 ? pvWarn() : p.faint);
      s += `<rect x="16" y="${y}" width="6" height="18" rx="2" fill="${lvl}"/><rect x="32" y="${y + 2}" width="${w}" height="7" rx="3" fill="${p.ink}"/><rect x="32" y="${y + 13}" width="${w * 0.55}" height="5" rx="2.5" fill="${p.faint}"/><text x="304" y="${y + 11}" text-anchor="end" font-family="JetBrains Mono,monospace" font-size="9" fill="${p.ink}">0${8 + i}:${10 + i * 7}</text>`; }
    return svgWrap(s); },
  agenda(p, seed) { const r = rng(seed); let s = `<line x1="48" y1="10" x2="48" y2="180" stroke="${p.faint}" stroke-width="2"/>`; const t = ['07:30', '08:15', '08:45', '09:00'];
    for (let i = 0; i < 4; i++) { const y = 22 + i * 42; const w = 150 + r() * 100; const hot = i === 2;
      s += `<text x="36" y="${y + 4}" text-anchor="end" font-family="JetBrains Mono,monospace" font-size="9.5" fill="${p.ink}">${t[i]}</text><circle cx="48" cy="${y}" r="${hot ? 6 : 4.5}" fill="${hot ? p.line : pvPaper()}" stroke="${p.line}" stroke-width="2"/><rect x="64" y="${y - 11}" width="${w}" height="22" rx="6" fill="${hot ? p.line : pvPaper()}" opacity="${hot ? .12 : 1}" stroke="${hot ? 'none' : p.faint}"/><rect x="74" y="${y - 3}" width="${w * .5}" height="6" rx="3" fill="${hot ? p.line : p.ink}"/>`; }
    return svgWrap(s); },
  doc(p) { let s = `<rect x="20" y="14" width="80" height="160" rx="4" fill="${pvPaper()}" stroke="${p.faint}"/>`; for (let i = 0; i < 7; i++) s += `<rect x="30" y="${28 + i * 18}" width="${i % 3 ? 42 : 60}" height="5" rx="2.5" fill="${i === 2 ? p.line : p.faint}"/>`;
    s += `<rect x="116" y="14" width="190" height="160" rx="4" fill="${pvPaper()}" stroke="${p.faint}"/><rect x="130" y="28" width="110" height="9" rx="3" fill="${p.ink}"/>`; for (let i = 0; i < 9; i++) s += `<rect x="130" y="${48 + i * 13}" width="${i % 4 === 3 ? 95 : 160}" height="5" rx="2.5" fill="${p.faint}"/>`; return svgWrap(s); },
  thumbs(p, seed) { const r = rng(seed); let s = ''; for (let i = 0; i < 6; i++) { const x = 14 + (i % 3) * 102, y = 14 + Math.floor(i / 3) * 80; s += `<rect x="${x}" y="${y}" width="92" height="56" rx="6" fill="${p.line}" opacity="${.12 + r() * .25}"/><path d="M${x + 40} ${y + 20} l14 8 l-14 8z" fill="${p.line}"/><rect x="${x}" y="${y + 62}" width="${50 + r() * 40}" height="5" rx="2.5" fill="${p.ink}"/>`; } return svgWrap(s); },
  table(p, seed) { const r = rng(seed); let s = `<rect x="0" y="0" width="320" height="26" fill="${p.line}" opacity=".08"/>`; const cols = [16, 90, 160, 230, 290];
    cols.forEach((x, i) => s += `<rect x="${x}" y="10" width="${i === 0 ? 44 : 34}" height="6" rx="3" fill="${p.ink}"/>`);
    for (let row = 0; row < 6; row++) { const y = 38 + row * 24; s += `<line x1="0" y1="${y + 16}" x2="320" y2="${y + 16}" stroke="${p.faint}"/>`; cols.forEach((x, i) => { const num = i > 1; s += num ? `<text x="${x + 34}" y="${y + 10}" text-anchor="end" font-family="JetBrains Mono,monospace" font-size="9.5" fill="${i === 4 && r() > .5 ? pvDown() : p.ink}">${(r() * 900 + 100).toFixed(i === 4 ? 1 : 0)}</text>` : `<rect x="${x}" y="${y + 4}" width="${i ? 36 : 50}" height="6" rx="3" fill="${i ? p.faint : p.ink}"/>`; }); }
    return svgWrap(s); },
  avatars(p, seed) { const r = rng(seed); let s = ''; for (let i = 0; i < 5; i++) { const y = 22 + i * 32; s += `<circle cx="30" cy="${y}" r="11" fill="${p.line}" opacity="${.18 + i * .1}"/><rect x="52" y="${y - 8}" width="${60 + r() * 40}" height="7" rx="3.5" fill="${p.ink}"/><rect x="52" y="${y + 3}" width="${40 + r() * 30}" height="5" rx="2.5" fill="${p.faint}"/><rect x="${220 + r() * 30}" y="${y - 7}" width="${30 + r() * 40}" height="14" rx="7" fill="${i % 2 ? p.line : pvDown()}" opacity=".15"/>`; } return svgWrap(s); },
  bars(p, seed) { const r = rng(seed); let s = gridLines(p); for (let i = 0; i < 12; i++) { const h = 20 + r() * 120; const x = 16 + i * 25; s += `<rect x="${x}" y="${170 - h}" width="16" height="${h}" rx="3" fill="${i === 7 ? p.line : p.lineSoft}"/>`; } return svgWrap(s); },
  chat(p) { let s = ''; const rows = [[0, 180], [1, 220], [0, 120], [1, 240], [0, 90]]; let y = 14;
    rows.forEach(([side, w]) => { const x = side ? 320 - 16 - w : 16; s += `<rect x="${x}" y="${y}" width="${w}" height="${side ? 34 : 24}" rx="10" fill="${side ? p.line : pvPaper()}" opacity="${side ? .12 : 1}" stroke="${side ? 'none' : p.faint}"/>`; s += `<rect x="${x + 12}" y="${y + 9}" width="${w - 24}" height="6" rx="3" fill="${side ? p.line : p.ink}"/>`; if (side) s += `<rect x="${x + 12}" y="${y + 20}" width="${(w - 24) * .6}" height="5" rx="2.5" fill="${p.lineSoft}"/>`; y += (side ? 34 : 24) + 10; }); return svgWrap(s); },
  feed(p, seed) { const r = rng(seed); let s = ''; for (let i = 0; i < 3; i++) { const y = 12 + i * 56; s += `<rect x="14" y="${y}" width="292" height="46" rx="8" fill="${pvPaper()}" stroke="${p.faint}"/><circle cx="34" cy="${y + 16}" r="9" fill="${p.line}" opacity=".3"/><rect x="50" y="${y + 11}" width="60" height="6" rx="3" fill="${p.ink}"/><rect x="116" y="${y + 12}" width="30" height="5" rx="2.5" fill="${p.faint}"/><rect x="50" y="${y + 26}" width="${150 + r() * 90}" height="5" rx="2.5" fill="${p.faint}"/><rect x="50" y="${y + 35}" width="${100 + r() * 60}" height="5" rx="2.5" fill="${p.faint}"/>`; } return svgWrap(s); },
  folders(p) { let s = ''; for (let i = 0; i < 6; i++) { const x = 18 + (i % 3) * 100, y = 22 + Math.floor(i / 3) * 76; s += `<path d="M${x} ${y + 8} h30 l8 8 h44 v46 h-82z" fill="${p.line}" opacity="${.12 + (i % 3) * .08}"/><rect x="${x}" y="${y + 16}" width="82" height="46" rx="3" fill="${pvPaper()}" stroke="${p.faint}"/><rect x="${x}" y="${y + 66}" width="${40 + i * 6}" height="5" rx="2.5" fill="${p.ink}"/>`; } return svgWrap(s); },
  pipeline(p) { let s = `<line x1="30" y1="90" x2="290" y2="90" stroke="${p.faint}" stroke-width="3"/>`; const st = ['檢索', '閱讀', '綜整', '報告']; st.forEach((t, i) => { const x = 40 + i * 80; const done = i < 2; s += `<circle cx="${x}" cy="90" r="14" fill="${done ? p.line : pvPaper()}" stroke="${p.line}" stroke-width="2"/>`; if (i === 2) s += `<circle cx="${x}" cy="90" r="6" fill="${p.line}"/>`; s += `<text x="${x}" y="126" text-anchor="middle" font-family="Noto Sans TC,sans-serif" font-size="11" fill="${p.ink}">${t}</text>`; });
    s += `<text x="160" y="46" text-anchor="middle" font-family="JetBrains Mono,monospace" font-size="11" fill="${p.line}">12:41 elapsed</text>`; return svgWrap(s); },
  grid(p) { let s = ''; for (let i = 0; i < 8; i++) { const x = 14 + (i % 4) * 76, y = 14 + Math.floor(i / 4) * 80; s += `<rect x="${x}" y="${y}" width="66" height="66" rx="10" fill="${pvPaper()}" stroke="${p.faint}"/><rect x="${x + 14}" y="${y + 14}" width="20" height="20" rx="5" fill="${p.line}" opacity="${.25 + (i % 3) * .2}"/><rect x="${x + 14}" y="${y + 44}" width="${28 + (i % 3) * 6}" height="5" rx="2.5" fill="${p.ink}"/>`; } return svgWrap(s); },
  menu(p, seed) { const r = rng(seed); let s = ''; for (let i = 0; i < 5; i++) { const y = 18 + i * 31; const on = i === 1 || i === 3; s += `<rect x="16" y="${y}" width="16" height="16" rx="4" fill="${on ? p.line : pvPaper()}" stroke="${on ? p.line : p.ink}" stroke-width="1.5"/>${on ? `<path d="M20 ${y + 8} l3 3 l6-6" stroke="${pvOnLine()}" stroke-width="2" fill="none"/>` : ''}<rect x="44" y="${y + 4}" width="${80 + r() * 90}" height="7" rx="3.5" fill="${p.ink}"/><text x="304" y="${y + 13}" text-anchor="end" font-family="JetBrains Mono,monospace" font-size="10" fill="${p.ink}">$${90 + Math.floor(r() * 80)}</text>`; } return svgWrap(s); },
  lessons(p) { let s = ''; for (let i = 0; i < 4; i++) { const y = 16 + i * 40; const p = [1, 1, .4, 0][i]; s += `<text x="16" y="${y + 14}" font-family="JetBrains Mono,monospace" font-size="10" fill="${p.ink}">0${i + 1}</text><rect x="40" y="${y + 5}" width="${120 + i * 20}" height="7" rx="3.5" fill="${p.ink}"/><rect x="40" y="${y + 20}" width="260" height="4" rx="2" fill="${p.faint}"/><rect x="40" y="${y + 20}" width="${260 * p}" height="4" rx="2" fill="${p.line}"/>`; } return svgWrap(s); },
  commits(p, seed) { const r = rng(seed); let s = ''; for (let row = 0; row < 7; row++) for (let c = 0; c < 20; c++) { const v = r(); s += `<rect x="${14 + c * 15}" y="${22 + row * 20}" width="12" height="12" rx="2.5" fill="${p.line}" opacity="${v < .4 ? .06 : v < .7 ? .25 : v < .9 ? .5 : .9}"/>`; } return svgWrap(s); },
  versions(p) { let s = ''; const v = ['v2.4.1', 'v2.4.0', 'v2.3.7', 'v2.3.6']; v.forEach((t, i) => { const y = 18 + i * 38; s += `<rect x="14" y="${y}" width="292" height="28" rx="6" fill="${pvPaper()}" stroke="${p.faint}"/><rect x="24" y="${y + 7}" width="52" height="14" rx="4" fill="${i ? p.faint : p.line}" opacity="${i ? 1 : .15}"/><text x="50" y="${y + 18}" text-anchor="middle" font-family="JetBrains Mono,monospace" font-size="9.5" fill="${i ? p.ink : p.line}">${t}</text><rect x="88" y="${y + 11}" width="${110 - i * 10}" height="6" rx="3" fill="${p.ink}"/><path d="M286 ${y + 8} v9 m-4 -4 l4 4 l4 -4" stroke="${p.line}" stroke-width="1.6" fill="none"/>`; }); return svgWrap(s); },
  calendar(p, seed) { const r = rng(seed); let s = ''; const d = ['一', '二', '三', '四', '五', '六', '日']; d.forEach((t, i) => s += `<text x="${34 + i * 42}" y="18" text-anchor="middle" font-family="Noto Sans TC,sans-serif" font-size="9.5" fill="${p.ink}">${t}</text>`);
    for (let i = 0; i < 35; i++) { const x = 14 + (i % 7) * 42, y = 26 + Math.floor(i / 7) * 30; const wk = i % 7 >= 5; const v = r(); const fill = wk ? 'transparent' : (v > .85 ? pvDown() : v > .7 ? pvWarn() : p.line); s += `<rect x="${x}" y="${y}" width="38" height="26" rx="4" fill="${fill}" opacity="${wk ? 0 : (v > .7 ? .35 : .14)}" stroke="${p.faint}"/>`; } return svgWrap(s); },
  checklist(p) { let s = ''; const st = [p.line, p.line, pvWarn(), p.faint]; for (let i = 0; i < 4; i++) { const y = 20 + i * 38; s += `<circle cx="26" cy="${y + 8}" r="8" fill="${st[i]}" opacity="${i === 3 ? 1 : .9}"/>${i < 2 ? `<path d="M22 ${y + 8} l3 3 l5-5" stroke="${pvOnLine()}" stroke-width="2" fill="none"/>` : ''}<rect x="46" y="${y + 4}" width="${110 + i * 15}" height="7" rx="3.5" fill="${p.ink}"/><rect x="46" y="${y + 16}" width="70" height="5" rx="2.5" fill="${p.faint}"/>`; } return svgWrap(s); },
  receipt(p) { let s = `<path d="M60 12 h200 v156 l-10-8 -10 8 -10-8 -10 8 -10-8 -10 8 -10-8 -10 8 -10-8 -10 8 -10-8 -10 8 -10-8 -10 8 -10-8 -10 8 -10-8 -10 8 -10-8 -10 8 -10-8 -10 8z" fill="${pvPaper()}" stroke="${p.faint}"/>`; for (let i = 0; i < 5; i++) { const y = 38 + i * 20; s += `<rect x="78" y="${y}" width="${60 + i * 8}" height="6" rx="3" fill="${p.faint}"/><text x="242" y="${y + 7}" text-anchor="end" font-family="JetBrains Mono,monospace" font-size="9.5" fill="${p.ink}">${(120 + i * 37).toLocaleString()}</text>`; } s += `<line x1="78" y1="142" x2="242" y2="142" stroke="${p.ink}"/><text x="242" y="158" text-anchor="end" font-family="JetBrains Mono,monospace" font-size="11" font-weight="500" fill="${p.line}">NT$ 1,085</text>`; return svgWrap(s); },
  payslip(p) { let s = ''; const rows = [['本薪', 52000, p.line], ['固定加項', 3000, p.line], ['午餐津貼', 2200, p.line], ['請假扣款', -1733, pvDown()], ['勞健保代扣', -2685, pvDown()]]; rows.forEach(([t, v, c], i) => { const y = 18 + i * 26; s += `<text x="16" y="${y + 12}" font-family="Noto Sans TC,sans-serif" font-size="11" fill="${p.ink}">${t}</text><line x1="90" y1="${y + 16}" x2="300" y2="${y + 16}" stroke="${p.faint}" stroke-dasharray="2 3"/><text x="304" y="${y + 12}" text-anchor="end" font-family="JetBrains Mono,monospace" font-size="11" fill="${c}">${v < 0 ? '−' : ''}${Math.abs(v).toLocaleString()}</text>`; }); s += `<line x1="16" y1="152" x2="304" y2="152" stroke="${p.ink}"/><text x="16" y="170" font-family="Noto Sans TC,sans-serif" font-size="11" font-weight="700" fill="${p.ink}">實發</text><text x="304" y="170" text-anchor="end" font-family="JetBrains Mono,monospace" font-size="13" font-weight="500" fill="${p.line}">52,782</text>`; return svgWrap(s); },
  toggles(p) { let s = ''; for (let i = 0; i < 5; i++) { const y = 20 + i * 30; const on = i !== 3; s += `<rect x="16" y="${y + 3}" width="${90 + (i % 3) * 20}" height="7" rx="3.5" fill="${p.ink}"/><rect x="270" y="${y}" width="34" height="16" rx="8" fill="${on ? p.line : p.faint}"/><circle cx="${on ? 296 : 278}" cy="${y + 8}" r="6" fill="${pvPaper()}"/>`; } return svgWrap(s); },
  log(p, seed) { const r = rng(seed); let s = ''; for (let i = 0; i < 7; i++) { const y = 14 + i * 23; s += `<text x="14" y="${y + 10}" font-family="JetBrains Mono,monospace" font-size="9" fill="${p.ink}">09-1${i} 1${i}:0${i}</text><rect x="100" y="${y + 3}" width="${40 + r() * 30}" height="6" rx="3" fill="${p.line}" opacity=".5"/><rect x="180" y="${y + 3}" width="${60 + r() * 60}" height="6" rx="3" fill="${p.faint}"/>`; } return svgWrap(s); },
};

/* ─────────────  UI 元件  ───────────── */

// Expressive: chip tone → GxCore.tone kind (plain→neutral, new→filled tertiary, acc→accent, lock→down).
const GX_CHIP_TONE = { plain: 'neutral', new: 'new', acc: 'accent', lock: 'down' };

function Chip({ tone, children }) {
  const tones = {
    plain: { background: VA2.bg, color: VA2.inkFaint, border: `1px solid ${VA2.rule}` },
    new:   { background: '#F5EBDC', color: VA2.warn, border: '1px solid transparent' },
    acc:   { background: VA2.accentSoft, color: VA2.accent, border: '1px solid transparent' },
    lock:  { background: '#FAF1F0', color: VA2.down, border: '1px solid #E9D6D5' },
  };
  const gxTone = GxCore.tone(GX_CHIP_TONE[tone] || 'neutral');
  return (
    <span style={uiStyle({
      fontFamily: VA2.mono, fontSize: FontSizes.trading_sm, letterSpacing: '0.06em',
      padding: '2px 7px', borderRadius: 5, ...(tones[tone] || tones.plain),
    }, {
      fontFamily: GxT.font, fontSize: 11.5, fontWeight: 700, letterSpacing: 0,
      padding: '3px 8px', borderRadius: GxT.radius.chipSm, border: 'none',
      background: gxTone.bg, color: gxTone.fg,
    })}>{children}</span>
  );
}

// 靜態導覽資料：不讀取目的系統或使用者資料。
const PREVIEWS = {
  'morning-focus': { summary: '依日期閱讀盤前多空新聞，分區掌握利多與利空重點。', highlights: ['選擇每日盤前文章', '分區閱讀利多與利空新聞', '在原站確認來源內容'], firstStep: '先選日期文章，再閱讀利多與利空新聞分區。' },
  fendian: { summary: '一頁看完前一交易日的分點籌碼：高手券商榜、自選券商買賣超、共同動向與大盤籌碼。', highlights: ['看波段常勝軍名次變動與連續上榜', '對照四家自選券商買賣超（含反指標）', '查看多家券商同向的共同動向個股'], firstStep: '先看頂部資料新鮮度，確認榜單已匯入，再往下讀各區塊。' },
  // TJCoach 是外部服務（一次性 token POST 換登入），本 repo 無可渲染的頁面 → 只提供文字預覽、不附截圖
  tjcoach: { summary: '匯入自己的交易資料，回頭檢視交易紀錄、績效與檢討筆記。', highlights: ['匯入個人交易資料', '檢視交易紀錄與績效統計', '逐筆撰寫與回顧交易檢討'], firstStep: '點卡片會在新分頁自動登入 TJCoach，先匯入近期的交易資料再開始檢討。' },
  'stock-studio': { summary: '圍繞同一檔股票，集中查看不同面向的資訊。可用資料依帳號權限。', highlights: ['查看價格與成交量', '閱讀基本面資料', '交叉參考籌碼與法人動向'], firstStep: '進入後輸入股票代號，選擇想查看的資訊。' },
  ytkb: { summary: '把影音內容整理成可以回顧的研究材料。', highlights: ['瀏覽影片與主題', '查看整理後的教材', '依頻道尋找相關內容'], firstStep: '選一個頻道或主題，開啟感興趣的影片教材。' },
  'research-engine': { summary: '提交需要深入整理的問題，追蹤研究任務與報告。', highlights: ['提交研究問題', '追蹤長時間任務進度', '查看與下載研究報告'], firstStep: '先整理研究問題與範圍，再提交任務；完成需要一些時間。' },
  'daily-brief': {
    summary: '快速瀏覽市場新聞，從標題掌握重點後再閱讀摘要。', highlights: ['依分類或關鍵字找新聞', '切換時間排序', '閱讀摘要並開啟原文'], firstStep: '先挑一則關注的標題，閱讀摘要並確認來源。',
  },
  'morning-report': {
    summary: '依日期瀏覽早晨報告，開啟閱讀畫面查看可用內容。', highlights: ['選擇報告日期', '閱讀已完成的報告', '切換可用的中文原文與原文'], firstStep: '先選日期與報告，再於閱讀畫面切換內容分頁。',
  },
  'analysis-report': {
    summary: '依日期與標的找到券商研究報告。', highlights: ['瀏覽日期分頁', '選擇研究標的', '切換可用的中英文內容'], firstStep: '先選日期或標的，再開啟需要閱讀的報告。',
  },
  'market-calendar': {
    summary: '一張月曆看完台股與美股的經濟數據、財報與公司事件、除權息與休市日。', highlights: ['依市場與事件類型篩選', '切換月／日檢視', '點事件看細節與官方來源'], firstStep: '先切到本週，看有哪些美國數據與台股法說會。',
  },
  quantdata: {
    summary: '查詢量化資料表，集中查看研究需要的數據。', highlights: ['選擇資料類別', '查看查詢表格', '閱讀資料儀表板'], firstStep: '先選需要的資料表，再設定查詢範圍。',
  },
  insider: {
    summary: '追蹤董監等內部人的持股申報異動。', highlights: ['依公司查看申報', '依申報人追蹤異動', '比較近期買賣紀錄'], firstStep: '先選公司或申報人，閱讀最近一期異動。',
  },
  'txo-board': {
    summary: '看台指選擇權各履約價的未平倉與 signed GEX 分布，判讀多空籌碼位置。', highlights: ['沿履約價梯形圖看買賣權未平倉', '對照 ATM 附近 signed GEX 方向', '看板每分鐘自動刷新'], firstStep: '先找到現貨所在的 ATM 列，再往上下看 GEX 集中的履約價。',
  },
  'fund-radar': {
    summary: '彙整國內股票型與平衡型基金每月公布的前十大持股，看哪些股票是基金共識、資金往哪些產業移動。', highlights: ['共識總覽：被最多基金持有的股票', '個股頁：哪些基金持有、持股變化', '基金列表：單一基金的前十大持股'], firstStep: '先選資料月份與基金池，從共識總覽點進有興趣的個股。',
  },
  bigday: {
    summary: '每晚收盤後掃描台灣 50 成分股，列出明天可能出現大波動的股票（強／中兩級），附回測、樣本外與實戰三個數字。', highlights: ['今日提示：明天可能大波動的股票與可信度', 'K 線與觀察價位：進入各級的價差與量門檻、部位大小與停損距離', '歷史與實戰成績：每天的提示與事後對帳'], firstStep: '先看今日提示有沒有「已驗證」的強級訊號，再到 K 線與觀察價位看明天的門檻。',
  },
  'kelly-game': {
    summary: '透過互動練習，觀察下注比例對資金的影響。', highlights: ['練習下注比例', '觀察資金曲線', '理解風險與報酬的關係'], firstStep: '先閱讀遊戲設定，再用小比例進行一輪練習。',
  },
  scanner: {
    summary: '依條件掃描市場，整理候選標的與部位投票。', highlights: ['設定掃描條件', '查看候選名單', '參考部位投票'], firstStep: '先設定篩選條件，再檢視符合條件的標的。',
  },
  research: {
    summary: '建立 AI 研究任務，追蹤執行狀態並閱讀完成結果。', highlights: ['提交研究問題', '查看任務狀態與詳情', '閱讀研究結果'], firstStep: '新增研究任務，填寫具體問題後送出，再從任務清單查看進度。',
  },
  'expert-tracker': {
    summary: '集中閱讀追蹤作者的最新貼文與影音動態。', highlights: ['查看追蹤名單', '閱讀 X 與 YouTube 動態', '把感興趣的內容送去研究'], firstStep: '選一位追蹤作者，閱讀最近的貼文。',
  },
  files: {
    summary: '瀏覽文件目錄，透過檔名搜尋找到需要的內容。', highlights: ['依資料夾瀏覽', '跨目錄搜尋檔名', '預覽支援的文件內容'], firstStep: '先選資料夾或輸入檔名關鍵字，再選擇要預覽的檔案。',
  },
  'ai-center': {
    summary: '查看 AI 工具介紹，區分已可開啟的影音知識庫與開發中的實驗室功能。', highlights: ['瀏覽工具介紹', '開啟影音知識庫', '了解實驗室功能的開發狀態'], firstStep: '先查看工具說明；影音知識庫可開啟，實驗室卡片目前顯示開發中提示。',
  },
  lunch: {
    summary: '查看今日菜單，選擇餐點並核對自己的訂單。', highlights: ['閱讀當日菜單', '點選餐點下單或填寫備註', '查看訂單與付款狀態'], firstStep: '先確認餐點與金額；點菜單卡片即可下單，需要備註時使用備註下單。',
  },
  courses: {
    summary: '開啟內部課程教材，查看各檔案的學習完成狀態。', highlights: ['瀏覽課程與影片', '選擇課程教材檔案', '查看已完成的教材數'], firstStep: '選擇一門課程，再從教材清單開啟尚未完成的內容。',
  },
  github: {
    summary: '前往 GSINVEST 的 GitHub 組織，查看有權存取的專案。', highlights: ['瀏覽組織專案', '閱讀專案說明', '查看程式與協作紀錄'], firstStep: '以有權限的 GitHub 帳號登入，再選擇需要的 repository。',
  },
  'release-hub': {
    summary: '依產品查看軟體版本紀錄與可用安裝檔。', highlights: ['選擇內部軟體產品', '閱讀版本紀錄', '下載適合的安裝檔'], firstStep: '先找到產品，再確認版本說明與適用環境。',
  },
  attendance: {
    summary: '檢查自己的打卡與出勤狀態，掌握需要補正的紀錄。', highlights: ['查看每月出勤', '辨識遲到或缺卡', '對照請假與工時'], firstStep: '先選月份，再查看有異常提示的日期。',
  },
  leave: {
    summary: '填寫請假區間並送審，追蹤自己的申請狀態。', highlights: ['選擇假別', '填寫日期與時段', '查看審核歷程'], firstStep: '確認假別與時間區間，再填寫申請資料。',
  },
  expense: {
    summary: '整理收據與支出，核對辨識內容後送出報帳。', highlights: ['上傳收據', '確認 OCR 金額', '逐筆送審'], firstStep: '先上傳清晰收據，再核對辨識出的金額與內容。',
  },
  calendar: {
    summary: '查看公司行程與系統設定的國定假日，安排工作。', highlights: ['切換月份與列表檢視', '確認假日', '查看事件時間與地點'], firstStep: '切換到需要的月份，查看目標日期的事件。',
  },
  employees: {
    summary: '管理員工名冊與基本的人事設定。', highlights: ['查看員工資料', '維護到離職資訊', '確認部門與角色'], firstStep: '先搜尋目標員工，再開啟資料核對。',
  },
  departments: {
    summary: '維護部門清單與主管指派。', highlights: ['查看部門代碼與名稱', '篩選啟用中的部門', '管理主管指派'], firstStep: '先在部門清單找到單位，再開啟編輯確認名稱、主管與狀態。',
  },
  reports: {
    summary: '依需求產生出勤、薪資與請假統計報表。', highlights: ['選擇報表類型', '設定統計期間', '匯出可用報表'], firstStep: '先選報表類型與期間，再確認匯出內容。',
  },
  payroll: {
    summary: '逐項查看月薪資單中的加減項與實發金額。', highlights: ['選擇薪資月份', '查看本薪與加減項', '核對實發金額'], firstStep: '先選月份，再展開自己的薪資明細。',
  },
  invoices: {
    summary: '按期別檢視進銷項發票與總帳分錄。', highlights: ['依期別篩選', '檢視進銷項資料', '核對總帳分錄'], firstStep: '先選期別，再切換要查看的發票或總帳內容。',
  },
  roles: {
    summary: '查看系統角色，依管理權限維護角色與帳號指派。', highlights: ['查看角色名稱與描述', '管理使用者角色指派', '管理非員工帳號'], firstStep: '先確認角色說明；需要指派角色時，切換到有權使用的帳號管理分頁。',
  },
  audit: {
    summary: '追查帳號在何時對哪些物件進行變更。', highlights: ['依帳號篩選', '查看操作時間', '追蹤變更物件'], firstStep: '先設定查詢期間或帳號，再閱讀對應紀錄。',
  },
  'admin-tools': {
    summary: '查看 AI 任務的 Token 使用量與使用者排行。', highlights: ['查看今日與累計 Token', '查看今日與累計任務數', '比較使用者 Token 用量'], firstStep: '先查看今日與累計統計，再閱讀下方使用者 Token 排行。',
  },
  'progress-ledger': {
    summary: '把 Slack 站會回報彙整成團隊進度與焦點。', highlights: ['閱讀站會回報', '查看週報與月報', '追蹤團隊焦點'], firstStep: '先選要查看的期間，再閱讀團隊回報與焦點事項。',
  },
  'trader-report': {
    summary: '填寫每周稽核表，查看繳交紀錄與主管簽核結果。', highlights: ['填寫稽核表', '查看繳交紀錄', '主管逐份簽核'], firstStep: '交易員填寫並提交，主管在簽核表中逐份檢閱。',
  },
  'llm-judge': {
    summary: '集中閱讀模型評測執行結果與缺陷資訊。', highlights: ['瀏覽評測 runs', '檢視缺陷', '比較 MoE 與 Colibri A/B 結果'], firstStep: '先選一筆評測 run，再閱讀結果與缺陷明細。',
  },
  'team-radar': {
    summary: '下載 AI 用量自查工具並上傳彙總；主管另以密碼登入查看開發協作能力雷達。', highlights: ['下載自查工具（Windows zip / pyz）', '上傳自查彙總交件', '主管端檢視協作能力雷達'], firstStep: '先下載自查工具在本機執行，再回到交件口上傳產出的彙總檔。',
  },
};

// 截圖由現有頁面元件渲染取得；無可用截圖時只提供文字，不重畫介面。
function FeaturePreviewImage({ featureKey, onEnlarge }) {
  const shot = (window.PORTAL_PREVIEW_IMAGES || {})[featureKey];
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [featureKey]);
  if (!shot || failed) return <p className="va2-preview-unavailable">{failed ? '頁面截圖暫時無法載入。' : '尚未提供此功能的實際頁面截圖。'}可先參考下方功能介紹。</p>;
  const picture = <img src={shot.src} alt={shot.alt} width={shot.width || 1440} height={shot.height || 900} onError={() => setFailed(true)} decoding="async" />;
  return <figure className="va2-preview-screenshot" data-preview-key={featureKey}>
    {onEnlarge ? <button type="button" className="va2-preview-enlarge" onClick={onEnlarge} aria-label="放大頁面截圖">{picture}<span>放大預覽 ↗</span></button> : picture}
    <figcaption>{shot.caption || '實際介面・示範資料'}{shot.capturedAt && <> · <time dateTime={shot.capturedAt}>{shot.capturedAt}</time></>}</figcaption>
  </figure>;
}
window.FeaturePreviewImage = FeaturePreviewImage;

function Tile({ item, seed, toast, palette }) {
  GxCore.useTheme();  // thumbnails embed concrete theme colours (pvMktColors / pvDown / pvWarn)
  const featureKey = item.previewKey || item.key;
  const preview = PREVIEWS[featureKey];
  const [previewMode, setPreviewMode] = React.useState(null);
  const [position, setPosition] = React.useState({ left: 12, top: 12 });
  const tileRef = React.useRef(null), panelRef = React.useRef(null), buttonRef = React.useRef(null);
  const openTimer = React.useRef(null), closeTimer = React.useRef(null);
  const previewId = `feature-preview-${featureKey}`;
  const clearTimers = () => { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); };
  const close = () => { clearTimers(); setPreviewMode(null); };
  const dismiss = () => {
    if (previewMode === 'pinned') buttonRef.current?.focus();
    close();
  };
  const announceOpen = () => window.dispatchEvent(new CustomEvent('portal-feature-preview-open', { detail: featureKey }));
  const show = () => { clearTimers(); announceOpen(); setPreviewMode((mode) => mode || 'hover'); };
  const scheduleClose = () => {
    clearTimeout(openTimer.current);
    if (previewMode === 'pinned') return;
    closeTimer.current = setTimeout(() => {
      if (!tileRef.current?.contains(document.activeElement) && !panelRef.current?.contains(document.activeElement)) setPreviewMode(null);
    }, 180);
  };
  React.useEffect(() => () => clearTimers(), []);
  React.useEffect(() => {
    const onOtherPreview = (e) => { if (e.detail !== featureKey) close(); };
    window.addEventListener('portal-feature-preview-open', onOtherPreview);
    return () => window.removeEventListener('portal-feature-preview-open', onOtherPreview);
  }, [featureKey]);
  React.useLayoutEffect(() => {
    if (!previewMode) return;
    const reposition = () => {
      if (previewMode === 'pinned' || !tileRef.current || !panelRef.current) return;
      const tile = tileRef.current.getBoundingClientRect(), panel = panelRef.current.getBoundingClientRect();
      const edge = 12, gap = 8;
      const clampLeft = (left) => Math.max(edge, Math.min(left, window.innerWidth - panel.width - edge));
      const clampTop = (top) => Math.max(edge, Math.min(top, window.innerHeight - panel.height - edge));
      let left = clampLeft(tile.left), top;
      if (tile.bottom + gap + panel.height <= window.innerHeight - edge) {
        top = tile.bottom + gap;
      } else if (tile.top - gap - panel.height >= edge) {
        top = tile.top - gap - panel.height;
      } else if (tile.right + gap + panel.width <= window.innerWidth - edge) {
        left = tile.right + gap;
        top = clampTop(tile.top);
      } else if (tile.left - gap - panel.width >= edge) {
        left = tile.left - gap - panel.width;
        top = clampTop(tile.top);
      } else {
        // 極窄視窗才使用最後回退；一般桌面優先四側避開原卡片。
        top = clampTop(tile.top - panel.height - gap);
      }
      setPosition({ left, top });
    };
    reposition();
    const observer = new ResizeObserver(reposition);
    if (panelRef.current) observer.observe(panelRef.current);
    if (previewMode === 'pinned') panelRef.current?.querySelector('button')?.focus();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); dismiss(); }
      if (previewMode === 'pinned' && e.key === 'Tab') {
        const targets = panelRef.current?.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]');
        if (!targets?.length) return;
        const first = targets[0], last = targets[targets.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { observer.disconnect(); window.removeEventListener('resize', reposition); window.removeEventListener('scroll', reposition, true); document.removeEventListener('keydown', onKey); };
  }, [previewMode]);
  const href = item.hash || item.href;
  const onClick = item.open
    ? (e) => { if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return; e.preventDefault(); close(); const fn = window[item.open]; if (typeof fn === 'function') fn(toast); }
    : close;
  const chips = [];
  (item.b || []).forEach((x) => chips.push(<Chip key={x} tone={x === 'NEW' ? 'new' : 'plain'}>{x}</Chip>));
  if (item.ext) chips.push(<Chip key="ext">↗ 新分頁</Chip>);
  if (item.lock) chips.push(<Chip key="lock" tone="lock">員工限定</Chip>);
  if (item.admin) chips.push(<Chip key="admin" tone="acc">僅 admin</Chip>);
  return (
    <div className="va2-cat-tile-wrap" ref={tileRef}
      onMouseEnter={preview ? () => { clearTimers(); if (window.matchMedia('(hover: hover)').matches) openTimer.current = setTimeout(show, 400); } : undefined}
      onMouseLeave={preview ? scheduleClose : undefined}
      onBlur={preview ? (e) => { if (!e.currentTarget.contains(e.relatedTarget) && !panelRef.current?.contains(e.relatedTarget)) scheduleClose(); } : undefined}
    >
    <a
      className="va2-cat-tile"
      href={href}
      target={item.ext ? '_blank' : undefined}
      rel={item.ext ? 'noopener noreferrer' : undefined}
      onClick={onClick}
      onFocus={preview ? show : undefined}
      aria-describedby={previewMode ? `${previewId}-summary` : undefined}
    >
      <div className="va2-cat-pv" dangerouslySetInnerHTML={{ __html: PV[item.p](palette, seed) }} />
      <div className="va2-cat-tx">
        <div className="va2-cat-tt" style={uiStyle({ fontSize: 14.5, fontWeight: 600, color: VA2.ink }, { fontSize: 15, fontWeight: 800 })}>{item.n}</div>
        <div className="va2-cat-td" style={{ color: VA2.inkSoft }}>{item.d}</div>
        {chips.length > 0 && <div className="va2-cat-chips">{chips}</div>}
      </div>
      {/* .va2-cat-go: Expressive look handled here only (inline), not in gx-css-p-cathub */}
      <span className="va2-cat-go" style={uiStyle({ border: `1px solid ${VA2.rule}`, color: VA2.inkSoft },
        { border: 'none', borderRadius: GxT.radius.inner, background: GxT.color.s2, width: 32, height: 32 })}>
        <Icon name="arrow-up-right" size={13} stroke={2} />
      </span>
    </a>
    {preview && <button ref={buttonRef} className="va2-cat-preview-trigger" type="button"
      aria-label={`預覽${item.n}`} aria-haspopup="dialog" aria-expanded={!!previewMode}
      aria-controls={previewMode ? previewId : undefined}
      onClick={() => { clearTimers(); announceOpen(); setPreviewMode('pinned'); }}>預覽</button>}
    {preview && previewMode && ReactDOM.createPortal(
      <div className={previewMode === 'pinned' ? 'va2-preview-backdrop' : 'va2-preview-layer'}
        onMouseDown={previewMode === 'pinned' ? (e) => { if (e.target === e.currentTarget) dismiss(); } : undefined}>
        <section id={previewId} ref={panelRef} className={'va2-feature-preview' + ((window.PORTAL_PREVIEW_IMAGES || {})[featureKey] ? ' has-screenshot' : '')}
          role={previewMode === 'pinned' ? 'dialog' : 'region'} aria-modal={previewMode === 'pinned' ? true : undefined}
          aria-labelledby={`${previewId}-title`} style={previewMode === 'pinned' ? undefined : position}
          onMouseEnter={clearTimers} onMouseLeave={scheduleClose}
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget) && !tileRef.current?.contains(e.relatedTarget)) scheduleClose(); }}>
          <div className="va2-feature-preview-head"><h2 id={`${previewId}-title`}>{item.n}</h2>
            <button type="button" onClick={dismiss} aria-label="關閉功能預覽">×</button></div>
          <FeaturePreviewImage featureKey={featureKey} onEnlarge={previewMode === 'hover' ? () => { clearTimers(); setPreviewMode('pinned'); } : undefined} />
          <p id={`${previewId}-summary`}>{preview.summary}</p>
          <ul>{preview.highlights.map((text) => <li key={text}>{text}</li>)}</ul>
          <p className="va2-feature-preview-start"><strong>從這裡開始</strong>{preview.firstStep}</p>
          {item.ext && <p className="va2-feature-preview-caption">開啟功能時會另開分頁</p>}
        </section>
      </div>, document.body)}
    </div>
  );
}

function SubHead({ name }) {
  return (
    <div className="va2-cat-subhead">
      <span style={uiStyle({
        fontFamily: VA2.mono, fontSize: FontSizes.trading_lg, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: VA2.inkFaint,
      }, {
        fontFamily: GxT.font, fontSize: 13, fontWeight: 800, letterSpacing: 0,
        textTransform: 'none', color: GxT.color.onV,
      })}>{name}</span>
      <i style={{ flex: 1, height: 1, background: VA2.rule }} />
    </div>
  );
}

function EmptyCategory({ cat }) {
  return (
    <div style={uiStyle({
      border: `1px dashed ${VA2.rule}`, borderRadius: 14, padding: 40, textAlign: 'center',
      background: VA2.surface, color: VA2.inkFaint, fontSize: FontSizes.sm,
    }, { border: 'none', borderRadius: GxT.radius.card, color: GxT.color.onV })}>
      <b style={uiStyle({ display: 'block', color: VA2.ink, fontFamily: VA2.serif, fontSize: 22, fontWeight: 600, marginBottom: 8 },
        { fontWeight: GxT.weight.display })}>
        {cat ? cat.name : '找不到這個分區'}
      </b>
      {cat ? '此分區目前沒有你可用的功能。' : '請從側欄選擇一個功能分區。'}
      <div style={{ marginTop: 14 }}>
        <a href="#/hub" style={uiStyle({ color: VA2.accent, fontSize: FontSizes.xs }, { fontWeight: 700 })}>回分析工作台 →</a>
      </div>
    </div>
  );
}

/* ─────────────  頁面本體  ───────────── */

function CategoryHubPage() {
  GxCore.useTheme();  // paletteOf() bakes concrete theme colours into the thumbnails (passed to Tile)
  const user = window._USER;
  const toast = (typeof useToast === 'function') ? useToast() : null;
  // #/cat/daily → #/cat/quant 時 _router 的 route 都是 'cat'（同值不重渲），
  // 所以本頁自己追 hash 第二段。
  const [catKey, setCatKey] = React.useState(currentKey);
  React.useEffect(() => {
    const onHash = () => setCatKey(currentKey());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const cat = CATEGORIES.find((c) => c.key === catKey);
  const visible = cat ? visibleItems(user, cat.key) : [];

  // #/cat（無第二段）→ 導到第一個看得到的分區；全都看不到就回落地頁
  React.useEffect(() => {
    if (catKey !== '') return;
    const first = CATEGORIES.find((c) => visibleItems(user, c.key).length > 0);
    window.location.hash = first ? `#/cat/${first.key}` : '#/';
  }, [catKey]);

  if (!cat || visible.length === 0) {
    return <div className="va2-cat-page"><EmptyCategory cat={cat} /></div>;
  }

  const n = visible.length;
  const ext = visible.filter((i) => i.ext).length;
  const lock = visible.filter((i) => i.lock || i.admin).length;
  const isVis = (it) => visible.includes(it);
  // 分區色：palette 給 SVG 縮圖；CSS 變數掛在 bento 容器，卡片邊框 hover／標題色塊／
  // 縮圖底色由 index.html 的 va2-cat-* 讀變數，不必每張卡 inline。
  const palette = paletteOf(cat);
  const catVars = { '--va2-cat-hue': palette.hue, '--va2-cat-tint': uiPick(cat.tint || '#F2F0EA', `var(--gx-${cat.key}-bg)`) };

  // Expressive: header becomes a tonal card in the category tint (mockup .cat-h); colours come
  // from the shell's [data-gx-t] vars (--gx-cbg / --gx-con / --gx-cacc), see gx-css-p-cathub.
  return (
    <div className="va2-cat-page" data-gx-t={uiPick(undefined, cat.key)}>
      <header className="va2-cat-head">
        {gxUiOn() && <span className="gx-cathub-ic" aria-hidden="true"><Icon name={cat.icon} size={24} /></span>}
        <div>
          <div style={uiStyle({
            fontFamily: VA2.mono, fontSize: FontSizes.trading_lg, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: palette.hue, marginBottom: 10,
          }, {
            fontFamily: GxT.font, fontSize: 13, fontWeight: 700, letterSpacing: 0,
            textTransform: 'none', color: 'inherit', marginBottom: 4,
          })}>{cat.eyebrow}</div>
          <h1 style={uiStyle({
            fontFamily: VA2.serif, fontWeight: 600, fontSize: 34, lineHeight: 1.15,
            margin: '0 0 10px', letterSpacing: '-0.005em', color: VA2.ink, textWrap: 'balance',
          }, {
            fontWeight: GxT.weight.display, fontSize: GxT.size.h1, lineHeight: 1.12,
            margin: '0 0 8px', letterSpacing: '-0.5px', color: 'inherit',
          })}>{cat.name}</h1>
          <p style={uiStyle({ color: VA2.inkSoft, fontSize: FontSizes.base, maxWidth: '60ch', margin: 0, lineHeight: 1.55 },
            { color: 'inherit', fontSize: 14, fontWeight: 500 })}>{cat.lead}</p>
        </div>
        <div className="va2-cat-meta" style={{ color: uiPick(VA2.inkFaint, 'inherit') }}>
          <span><b>{n}</b> 個功能</span>
          <span><b>{ext}</b> 個外開</span>
          <span><b>{lock}</b> 個受限</span>
        </div>
      </header>

      {cat.items ? (
        <div className="va2-cat-bento" style={catVars}>
          {cat.items.map((it, i) => isVis(it) && <Tile key={it.n} item={it} seed={i + 1} toast={toast} palette={palette} />)}
        </div>
      ) : (
        <div className="va2-cat-bento" style={catVars}>
          {cat.sections.map((s, si) => {
            const items = s.items.filter(isVis);
            if (!items.length) return null;
            return (
              <React.Fragment key={s.name}>
                <SubHead name={s.name} />
                {items.map((it) => <Tile key={it.n} item={it} seed={si * 10 + s.items.indexOf(it) + 1} toast={toast} palette={palette} />)}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

window.CategoryHub = { CATEGORIES, QUICK_ACCESS_KEYS, findItem, itemVisible, visibleItems, categoryOfRoute, currentKey };
window.CategoryHubPage = CategoryHubPage;

})();
